import { useEffect } from 'react'
import type { ElementType, ReactNode } from 'react'
import './glass.css'
import { supportsLens } from './lens-support'

export type GlassLevel = 'frost' | 'denso' | 'lens' | 'flat'

type Props = {
  level: GlassLevel
  children: ReactNode
  className?: string
  as?: ElementType
  /**
   * **O resto vai para o elemento.** Sem isto, `as="button"` produzia um botão
   * mudo: `onClick`, `type` e `aria-current` eram aceitos pelo `as` e
   * **descartados** na renderização — o chip de mês não navegava, e o teste
   * reprovou com "expected null to be 'true'" no `aria-current`.
   *
   * Um primitivo de superfície que aceita virar `button` e não repassa o
   * `onClick` é uma armadilha silenciosa: compila, renderiza, e não faz nada.
   */
  [prop: string]: unknown
}

/** Liga data-lens no <html> uma vez, para o CSS decidir. */
function useLensFlag() {
  useEffect(() => {
    document.documentElement.dataset.lens = supportsLens() ? 'on' : 'off'
  }, [])
}

/**
 * Superfície de vidro em quatro níveis. Ver Docs/04-Design/Liquid Glass.md.
 *
 * | nível | o que é               | onde                              |
 * |-------|-----------------------|-----------------------------------|
 * | frost | desfoque + tint médio | o padrão: barras, cards           |
 * | denso | frost com véu escuro  | quando há TEXTO por cima          |
 * | lens  | refração, um passe    | bônus onde o navegador aceita     |
 * | flat  | sólido                | fallback e acessibilidade         |
 *
 * ## A refração NÃO é elemento de desenho (decisão 202)
 *
 * O WebKit não aceita `url()` em `backdrop-filter`, e o app é um PWA no
 * iPhone: no aparelho onde ele é usado todo dia, a refração **não existe**.
 * Projetar em torno dela seria projetar para o lugar errado.
 *
 * Então o iPhone é a renderização de **referência**, não o fallback. O que
 * carrega o material é o que funciona em todo lugar: o tint em gradiente, o
 * rim specular ao longo do perímetro, a sombra em três camadas e a tipografia.
 * O `lens` fica como bônus silencioso onde o Chromium o aceita — e nenhuma
 * tela pode depender dele para se explicar.
 *
 * ## O orçamento
 *
 * No máximo **6 frost** na viewport, nunca em item de lista; no máximo
 * **3 lens** por tela. Passar disso derrubou a composição do Chromium num
 * protótipo — a página inteira em branco, não "mais lento".
 *
 * ## A regra que decide o nível
 *
 * **Quanto mais há para ler, mais opaco o vidro.** O `denso` vai onde há um
 * parágrafo; o `frost` onde há um número e um rótulo. Vidro que atrapalha a
 * leitura é vidro no lugar errado.
 */
export function Glass({ level, children, className = '', as: Tag = 'div', ...rest }: Props) {
  useLensFlag()
  return (
    <Tag {...rest} className={`glass glass--${level} ${className}`.trim()}>
      {children}
    </Tag>
  )
}
